'use client';

/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect } from 'react';
import { twMerge } from 'utilities/src/twMerge';
import LoadingIndicator from 'ui/src/icons/LoadingIndicator';
import useForm from 'ui/src/hooks/useForm';

import useCreateForm from 'ui/src/components/Form';
import Button, { type IButtonProps } from 'ui/src/components/Button';
import { ThemeEnum } from 'ui/src/components/Theme';

import { validateForm } from 'utilities/src/validateSubscription';
import { MessageError } from 'ui/src/components/Form/shared/components';
import useFooterSubscriptionFormTheme from './themes/subscriptionForm/FooterSubscriptionForm.theme';

export type FooterSubscriptionFormProps<Theme extends ThemeEnum = ThemeEnum> = {
  subscriptionApi: string;
  subscriptionBtn: IButtonProps<Theme>;
  csrfToken?: string;
  label?: string;
  placeholder?: string;
};

export default function FooterSubscriptionForm<Theme extends ThemeEnum = ThemeEnum>({
  subscriptionApi,
  subscriptionBtn,
  csrfToken,
  label,
  placeholder,
}: FooterSubscriptionFormProps<Theme>) {
  const {
    subscriptionFormContainer,
    subscriptionCheckboxContainer,
    subscriptionFieldContainer,
    subscriptionButton,
    errorMessageClasses,
    labelClasses,
  } = useFooterSubscriptionFormTheme().classes;
  const currentFormTheme = useFooterSubscriptionFormTheme().theme;
  const [isSuscripted, setIsSuscripted] = useState(false);
  const { errorMessage, error, formSubmit, loading, successfullySent } = useForm({
    fetchUrl: subscriptionApi,
    csrfToken,
  });

  const [formFields, setFormFields] = useState({
    email: '',
    understoodTerms: false,
  });

  const { FormBuilder, FormInput, FormCheckbox } = useCreateForm<typeof formFields>();

  useEffect(() => {
    if (successfullySent) {
      setIsSuscripted(true);
    }
  }, [setIsSuscripted, successfullySent]);

  if (isSuscripted) {
    return (
      <>
        <p className="text-site-primary-600 site-paragraph-s font-bold">Thank you for subscribing.</p>
        <p className="text-sm">We&apos;ve sent you our most recent newsletter.</p>
      </>
    );
  }

  return (
    <div
      className={twMerge('max-w-xl items-center justify-center', {
        hidden: successfullySent,
      })}
    >
      <div
        className={twMerge('mx-auto mt-6 flex flex-col items-center justify-center text-center text-base', {
          hidden: !loading,
        })}
      >
        <LoadingIndicator />
        <br />
        <span className="text-white"> Sending information... </span>
      </div>
      <div
        className={twMerge({
          hidden: loading || successfullySent,
        })}
      >
        <div>
          {error && (
            <MessageError
              message={errorMessage}
              state="Error"
              theme={currentFormTheme}
              className={twMerge(errorMessageClasses)}
            />
          )}
        </div>
        <FormBuilder
          id="footer-subscription-form"
          onSubmit={formSubmit}
          className={twMerge(subscriptionFormContainer)}
        >
          <div>
            <FormInput.Text
              name="email"
              type="email"
              label={label}
              className="relative 2xl:!flex-row [&>label]:w-full"
              fieldClassName={twMerge(subscriptionFieldContainer)}
              placeholder={placeholder}
              required
              theme={currentFormTheme}
              maxLength={140}
              value={formFields.email}
              onChange={(e) => setFormFields({ ...formFields, email: e.target.value })}
              rules={validateForm.email}
            />
          </div>

          <FormCheckbox
            label={
              <span className={labelClasses}>
                By subscribing I accept the{' '}
                <a
                  href="https://www.bairesdev.com/privacy-policy/"
                  target="_blank"
                  className="terms-link underline"
                  rel="noreferrer noopener"
                >
                  Privacy Policy
                </a>
                .
              </span>
            }
            legend="Accept"
            name="understoodTerms"
            theme={currentFormTheme}
            className={twMerge(subscriptionCheckboxContainer)}
            onChange={(e) => setFormFields({ ...formFields, understoodTerms: e.target.checked })}
            rules={validateForm.understoodTerms}
          />
          <Button<Theme> {...subscriptionBtn} type="submit" size="Medium" classes={twMerge(subscriptionButton)}>
            Subscribe
          </Button>
        </FormBuilder>
      </div>
    </div>
  );
}
